import { momentDateTime } from '../dates';
import { useEffect, useRef, useState } from 'react';
import { activeOrganizationId, graphql } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { ResearchRunLog } from './ResearchRunLog';
import { uploadResearchEvidence } from '../api/documents';
import { ResearchCorrections } from './ResearchCorrections';
import { ResearchKnowledge } from './ResearchKnowledge';
import { ResearchReviewTrail } from './ResearchReviewTrail';
import { recipientLabel } from './recipient-labels';
import { displayCase } from './display-case';
import { ResearchRecord } from './ResearchRecord';
import { linkedToProperty } from './research-record';

type Owner = {
  parcel: string;
  owner: string;
  mailingAddress: string;
  siteAddress: string;
  addressMatch: string;
  sourceFile: string;
  sourceUrl?: string;
  sources?: Array<{ file: string; page?: number; quote?: string }>;
};
type Notice = {
  instrument: string;
  title: string;
  pdfFile: string;
  sourceUrl?: string;
  recordedOn?: string;
  indexRow?: string;
  indexUrl?: string;
  indexSourceFile?: string;
  matchingParcels?: string[];
  propertyMatchSources?: Array<{ file: string; page?: number; quote?: string }>;
  propertyMatch?: 'PARCEL_ID_MATCH' | 'STREET_ADDRESS_MATCH' | 'SOLE_CANDIDATE_OWNER_MATCH' | 'NOT_ESTABLISHED';
  fields: Array<{
    name: string;
    text: string;
    sources: Array<{ file: string; page?: number; quote?: string }>;
  }>;
};
type Result = {
  owners: Owner[];
  documents: Notice[];
  noticeRecipients?: Array<{ role: string; name: string; address: string; sourceFile: string }>;
  unresolvedRecipients?: Array<{ role: string; reason: string }>;
  flags: string[];
  summary: string;
  unansweredQuestions?: string[];
  limitations?: string[];
  operatorHandoff?: {
    reason: 'SOURCE_ACCESS_BLOCKED' | 'PROPERTY_ADDRESS_CONFLICT' | 'COUNTY_NOT_SUPPORTED' | 'PROPERTY_NOT_RESOLVED' | 'NOTICE_NOT_RETRIEVED' | 'RECIPIENTS_NOT_IDENTIFIED';
    stage: string;
    sourceUrl?: string;
    evidenceFile?: string;
    instructions: string[];
  };
  revalidations?: Array<{ versionId: string; reason: string; sources: Array<{ file: string; page?: number; quote: string }> }>;
};
const recipientKey = (recipient: { role: string; name: string }) => `${recipient.role}|${recipient.name}`;
type Confirmation = { certifiedAt: string; recipients: Array<{ role: string; name: string; address: string; source: 'RESEARCH' | 'CUSTOMER' }> };
type RuleCoverage = { reason: string; jurisdiction: string; documentType: string; heldAt: string };
const coverageReasons: Record<string, string> = {
  NO_MATCHING_RULE: 'No reviewed rule covers this state, job type and role yet.',
  AMBIGUOUS_RULES: 'More than one reviewed rule claims this job; the rules need correcting.',
  RULE_HAS_NO_RECIPIENTS: 'The rule for this job does not list who the notice goes to yet.',
  RULE_NOT_IN_FORCE: 'No reviewed rule for this job is in force today.',
  NO_DATING_RULE: 'No reviewed rule dates a notice for this state, job type and role yet.',
};
type Job = {
  id: string;
  status: string;
  strategy?: string;
  parentJobId?: string;
  instruction?: string;
  failureCode?: string;
  attemptCount: number;
  createdAt: string;
  input: { address: string; workDescription: string; state: string; county: string };
  result?: Result;
  artifacts: Array<{ id: string; fileName: string; sha256?: string }>;
  sourceCaptures?: Array<{ step: string; url: string; artifactId: string; fileName: string; sha256: string; capturedAt: string }>;
  reviews: Array<{
    decision: string;
    parcelIds: string[];
    instruments: string[];
    note: string;
    createdAt: string;
  }>;
};
const labels: Record<string, string> = {
  QUEUED: 'Waiting for a research worker',
  RUNNING: 'Searching public records',
  REVIEW_REQUIRED: 'Findings need review',
  FAILED: 'Research could not finish',
  ACCEPTED: 'Research accepted for quality review',
  NEEDS_INFORMATION: 'Additional details requested',
};
function warningLabel(flag: string): string {
  const labels: Record<string, string> = {
    LEGAL_APPLICABILITY_NOT_APPROVED:
      'Document applicability still needs review',
    AMENDED_DOCUMENT: 'An amended notice was found',
    MULTI_PARCEL_DOCUMENT: 'A notice covers multiple parcels',
    PARK_IMPROVEMENT_SCOPE_REVIEW: 'A notice describes park or greenway work',
    CURRENT_OWNER_NOT_CONFIRMED_IN_OWNER_SECTION:
      'Current owner was not confirmed in the notice owner section',
    PARCEL_ID_MATCH_NOT_ESTABLISHED:
      'A parcel-number match was not established',
    PARTY_EXTRACTION_INCOMPLETE: 'Some party details could not be extracted',
    JOB_WORK_SCOPE_REQUIRED: 'The job work description is missing',
    CLERK_SEARCH_NOT_EXHAUSTIVE: 'The clerk search may have additional results',
    PROPERTY_SEARCH_NOT_EXHAUSTIVE:
      'The property search may have additional results',
    NEARBY_PARCEL_IS_NOT_CONFIRMED_JOB_PROPERTY:
      'A nearby parcel has not been confirmed as the job property',
    PROPERTY_NOT_RESOLVED: 'The job property could not be identified',
    NO_NOC_RETRIEVED: 'No notice was retrieved',
    NO_NOC_RETRIEVED_IN_SEARCH: 'No notice was retrieved in the search',
    COUNTY_NOT_SUPPORTED: 'This county needs manual research',
    OPERATOR_HANDOFF_REQUIRED: 'An operator must complete the listed handoff',
    OPERATOR_COMPLETION_REQUIRES_REVIEW: 'Review the operator-completed evidence before using it',
    PROPERTY_ADDRESS_CONFLICT: 'The project and official parcel addresses conflict',
    RELATED_NOTICES_REQUIRE_REVIEW: 'Related recorded notices need review',
    DOCUMENT_INSTRUMENT_MISMATCH:
      'The PDF instrument number does not match the index',
    DOCUMENT_REVIEW_TRUNCATED: 'Some document pages were not processed',
    HUMAN_REVIEW_REQUIRED: 'A person needs to review these findings',
    AI_FINDINGS_REQUIRE_REVIEW: 'AI findings need checking against their sources',
    RESEARCH_INCOMPLETE: 'Research did not finish every step',
    REQUEST_BLOCKED_OR_FAILED: 'An official website blocked or failed a request',
    SOURCE_ACCESS_RESTRICTED: 'An official website restricted access',
    PARCEL_LAYER_NOT_CONFIGURED: 'The county property search could not be used',
    PRIOR_RESEARCH_CONTEXT_TRUNCATED: 'Earlier research was too long to pass on in full',
    PROPERTY_AND_DOCUMENT_MATCH_REQUIRE_REVIEW: 'Whether the notice covers this property needs checking',
  };
  if (flag.startsWith('JOB_ADDRESS_DIFFERS:'))
    return `Job address differs from parcel ${flag.split(':')[1]}`;
  const plain = flag.toLowerCase().replaceAll('_', ' ').replaceAll(':', ': ');
  return labels[flag] ?? plain.charAt(0).toUpperCase() + plain.slice(1);
}
// Flags that only say the findings need a person to look; they carry no reason when research found nothing.
const reviewOnlyFlags = new Set(['HUMAN_REVIEW_REQUIRED', 'AI_FINDINGS_REQUIRE_REVIEW', 'PROPERTY_AND_DOCUMENT_MATCH_REQUIRE_REVIEW', 'RESEARCH_INCOMPLETE']);
export function ResearchPanel({
  orderId,
  orderStatus,
  onChange,
}: {
  orderId: string;
  orderStatus: string;
  onChange: () => void;
}) {
  const { token } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const [parcels, setParcels] = useState<string[]>([]);
  const [instruments, setInstruments] = useState<string[]>([]);
  const [confirmedRecipients, setConfirmedRecipients] = useState<string[]>([]);
  const [flags, setFlags] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [aiEnabled, setAiEnabled] = useState(false);
  // Whether an AI run queued now would start, and whether a notice rule covers this job now.
  const [aiAvailable, setAiAvailable] = useState(false);
  const [ruleAvailable, setRuleAvailable] = useState(false);
  // What the customer confirmed for this research, which is what the notice uses.
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [instruction, setInstruction] = useState('');
  // When the customer is confirming, selections appear only if staff choose to act for them.
  const [actingForCustomer, setActingForCustomer] = useState(false);
  const followupRequest = useRef<{ instruction: string; key: string } | null>(null);
  const job = jobs[0];
  // Research told which recipients its rule requires is accepted by confirming them.
  const ruleApplies = !!(job?.input as { job?: { recipientRule?: unknown } } | undefined)?.job?.recipientRule;
  const [ruleCoverage, setRuleCoverage] = useState<RuleCoverage | null>(null);
  const loadedJob = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    async function load() {
      try {
        const data = await graphql<{ researchJobs: Job[]; researchAiEnabled: boolean; researchAiAvailable: boolean; researchRuleAvailable: boolean; researchRuleCoverage: RuleCoverage | null;
          noticeRecipients: { certification: Confirmation | null } | null }>({
          query:
            'query Research($id:ID!){noticeRecipients(orderId:$id){certification} researchAiEnabled researchAiAvailable researchRuleAvailable(orderId:$id) researchRuleCoverage(orderId:$id){reason jurisdiction documentType heldAt} researchJobs(orderId:$id){id status strategy parentJobId instruction attemptCount failureCode createdAt input result artifacts{id fileName sha256} sourceCaptures{step url artifactId fileName sha256 capturedAt} reviews{decision parcelIds instruments note createdAt}}}',
          variables: { id: orderId },
          token: token!,
          organizationId: activeOrganizationId(),
        });
        if (!cancelled) {
          if (loadedJob.current !== data.researchJobs[0]?.id) {
            loadedJob.current = data.researchJobs[0]?.id;
            setParcels([]);
            setInstruments([]);
            setConfirmedRecipients([]);
            setFlags([]);
            setNote('');
            setInstruction('');
            followupRequest.current = null;
          }
          setJobs(data.researchJobs);
          setRuleCoverage(data.researchRuleCoverage ?? null);
          setAiEnabled(data.researchAiEnabled === true);
          setConfirmation(data.noticeRecipients?.certification ?? null);
          setAiAvailable(data.researchAiAvailable === true);
          setRuleAvailable(data.researchRuleAvailable === true);
          setError('');
          setLoading(false);
        }
      } catch (reason) {
        if (!cancelled) {
          setError((reason as Error).message);
          setLoading(false);
        }
      }
    }
    void load();
    const timer = setInterval(() => void load(), 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [token, orderId, version]);
  function toggle(
    value: string,
    values: string[],
    set: (values: string[]) => void,
  ) {
    set(
      values.includes(value)
        ? values.filter((v) => v !== value)
        : [...values, value],
    );
  }
  async function retry() {
    if (!token) return;
    setBusy(true);
    setError('');
    try {
      await graphql({
        query:
          'mutation Retry($id:ID!,$key:String!){retryResearch(orderId:$id,requestKey:$key){id}}',
        variables: { id: orderId, key: crypto.randomUUID() },
        token,
        organizationId: activeOrganizationId(),
      });
      setParcels([]);
      setInstruments([]);
      setConfirmedRecipients([]);
      setFlags([]);
      setNote('');
      setVersion((v) => v + 1);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function followup() {
    if (!token || !job || !aiEnabled) return;
    setBusy(true); setError('');
    if (followupRequest.current?.instruction !== instruction) followupRequest.current = { instruction, key: crypto.randomUUID() };
    try {
      await graphql({
        query: 'mutation Further($parentJobId:ID!,$instruction:String!,$requestKey:String!){researchFurther(parentJobId:$parentJobId,instruction:$instruction,requestKey:$requestKey){id}}',
        variables: { parentJobId: job.id, instruction, requestKey: followupRequest.current.key }, token, organizationId: activeOrganizationId(),
      });
      setVersion(v => v + 1); onChange();
    } catch (reason) { setError((reason as Error).message); }
    finally { setBusy(false); }
  }
  async function completeManually(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !job) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const parcelFile = form.get('parcelFile'); const nocFile = form.get('nocFile');
    if (!(parcelFile instanceof File) || !(nocFile instanceof File)) return;
    setBusy(true); setError('');
    try {
      const parcelEvidenceId = await uploadResearchEvidence({ token, orderId, file: parcelFile });
      const nocEvidenceId = await uploadResearchEvidence({ token, orderId, file: nocFile });
      const value = (name: string) => String(form.get(name) ?? '').trim();
      await graphql({ query: 'mutation CompleteResearch($input:JSON!){submitOperatorResearchCompletion(input:$input){id status}}',
        variables: { input: { orderId, parentJobId: job.id, requestKey: crypto.randomUUID(), parcelEvidenceId,
          nocEvidenceId, owner: { parcel: value('parcel'), owner: value('owner'), mailingAddress: value('mailingAddress'),
            siteAddress: value('siteAddress'), addressMatch: value('addressMatch'), citation: { page: Number(value('parcelPage')), quote: value('parcelQuote') } },
          notice: { instrument: value('instrument'), title: value('title'), citation: { page: Number(value('nocPage')), quote: value('nocQuote') } },
          matchExplanation: value('matchExplanation') } }, token, organizationId: activeOrganizationId() });
      formElement.reset(); setVersion(v => v + 1); onChange();
    } catch (reason) { setError((reason as Error).message); }
    finally { setBusy(false); }
  }
  async function download(file: string) {
    if (!token || !job) return;
    const artifact = job.artifacts.find((a) => a.fileName === file);
    if (!artifact) {
      setError('The evidence file is unavailable.');
      return;
    }
    await downloadArtifact(artifact.id);
  }
  async function downloadArtifact(artifactId: string) {
    if (!token) return;
    try {
      const data = await graphql<{ researchArtifactDownload: string }>({
        query: 'query Download($id:ID!){researchArtifactDownload(id:$id)}',
        variables: { id: artifactId },
        token,
        organizationId: activeOrganizationId(),
      });
      const link = document.createElement('a');
      link.href = data.researchArtifactDownload;
      link.rel = 'noopener';
      link.click();
    } catch (reason) {
      setError((reason as Error).message);
    }
  }
  async function review(decision: string) {
    if (!token || !job) return;
    setBusy(true);
    setError('');
    try {
      await graphql({
        query:
          'mutation Review($jobId:ID!,$decision:String!,$parcelIds:[String!]!,$instruments:[String!]!,$acknowledgedFlags:[String!]!,$recipients:JSON,$note:String!){reviewResearch(jobId:$jobId,decision:$decision,parcelIds:$parcelIds,instruments:$instruments,acknowledgedFlags:$acknowledgedFlags,recipients:$recipients,note:$note){id status}}',
        variables: {
          jobId: job.id,
          decision,
          parcelIds: parcels,
          instruments,
          acknowledgedFlags: flags,
          recipients: (job.result?.noticeRecipients ?? []).filter(recipient => confirmedRecipients.includes(recipientKey(recipient)))
            .map(recipient => ({ role: recipient.role, name: recipient.name })),
          note,
        },
        token,
        organizationId: activeOrganizationId(),
      });
      setVersion((v) => v + 1);
      onChange();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const editable =
    orderStatus === 'RESEARCH' && job?.status === 'REVIEW_REQUIRED';
  const finished = !!job && ['REVIEW_REQUIRED', 'FAILED'].includes(job.status);
  // Whose turn it is decides what the page leads with.
  const hasFindings = !!job?.result && (job.result.owners.length > 0 || job.result.documents.length > 0 || !!job.result.noticeRecipients?.length);
  // Research that ran before a rule covered the job looks for the property, not for who the notice goes to.
  const predatesRule = !ruleApplies && ruleAvailable;
  const turn = !job ? (ruleCoverage ? 'rule' : ruleAvailable && orderStatus === 'RESEARCH' ? 'start' : 'none')
    : ['QUEUED', 'RUNNING'].includes(job.status) ? 'worker'
      : orderStatus !== 'RESEARCH' || !finished ? 'none'
        : predatesRule ? 'rerun' : !ruleApplies && !hasFindings ? 'empty' : ruleApplies ? 'customer' : 'staff';
  const turnTitle: Record<string, string> = { rule: 'Waiting for a notice rule', start: 'Research has not run', worker: 'Research is running',
    rerun: 'Run research with the notice rule', empty: 'Research found nothing to accept', customer: 'Waiting for the customer',
    staff: 'Staff decision needed', none: !job ? 'No research yet' : finished && orderStatus !== 'RESEARCH' ? 'Research is finished' : 'Nothing needed from staff' };
  const turnText: Record<string, string> = {
    rule: 'Approve one on Notice rules and research starts by itself.',
    start: 'A notice rule covers this job. Run research to find who the notice goes to.',
    worker: 'Nothing to do yet. This page updates when it finishes.',
    rerun: 'A notice rule now covers this job, but this research ran before it. Run it again to find who the notice goes to.',
    empty: 'It found no property owner or notice of commencement. Run it again, complete it with official documents, or ask the customer for more job details.',
    customer: 'The customer confirms who the notice goes to. Staff only step in if the customer asks for help.',
    staff: 'Check the property and notice of commencement below, then accept the findings or ask for more job details.',
    none: !job ? 'No research has run for this notice.'
      : finished && orderStatus !== 'RESEARCH' ? (confirmation ? 'The customer confirmed who the notice goes to, shown below.' : 'This notice has moved past research.')
        : `${labels[job.status] ?? job.status}.`,
  };
  // Running research is the next step in these states, so it leads the page.
  const runLeads = ['start', 'rerun', 'empty'].includes(turn);
  const selecting = turn === 'staff' || actingForCustomer;
  const recipientsDone = (job?.result?.noticeRecipients ?? []).every(recipient => confirmedRecipients.includes(recipientKey(recipient)));
  const warningsDone = (job?.result?.flags ?? []).every(f => flags.includes(f));
  const acceptBlocked = busy || !editable || !!job?.result?.revalidations?.length ||
    (ruleApplies
      ? !!job?.result?.unresolvedRecipients?.length ||
        (job?.result?.noticeRecipients ?? []).some(recipient => !confirmedRecipients.includes(recipientKey(recipient)))
      : !parcels.length || !instruments.length) ||
    note.trim().length < 10 || !!job?.result?.flags.some((f) => !flags.includes(f));
  const missing = [
    ...(ruleApplies ? [] : [!parcels.length && 'choose the job property', !instruments.length && 'choose the notice of commencement']),
    ...(ruleApplies && (job?.result?.noticeRecipients ?? []).some(recipient => !confirmedRecipients.includes(recipientKey(recipient))) ? ['confirm each recipient'] : []),
    (job?.result?.flags ?? []).some(f => !flags.includes(f)) && 'acknowledge each warning',
    note.trim().length < 10 && 'explain your decision in at least 10 characters',
  ].filter(Boolean) as string[];
  const canAccept = hasFindings && !predatesRule;
  const decision = <div className="research-decision" aria-label="Research decision">
    <h3>{canAccept ? 'Your decision' : 'Or ask the customer'}</h3>
    <label>
      {canAccept ? 'Explain the property and scope decision, or the missing information' : 'What the customer should check or add'}
      <textarea value={note} onChange={(e) => setNote(e.target.value)} minLength={10} />
    </label>
    {ruleApplies && <p>{job?.result?.unresolvedRecipients?.length
      ? 'Every required recipient must be identified before these findings can be accepted; request more information or run follow-up research.'
      : 'Confirm each recipient against its source. A parcel or notice selection is optional.'}</p>}
    {canAccept && missing.length > 0 && editable && <p className="decision-missing">To accept: {missing.join(', ')}.</p>}
    <div className="form-actions">
      {canAccept && <button className="button" disabled={acceptBlocked} onClick={() => void review('ACCEPT')}>Accept findings for quality review</button>}
      <button className="button button-quiet" disabled={busy || note.trim().length < 10} onClick={() => void review('REQUEST_INFORMATION')}>Request more job details</button>
    </div>
    {!(canAccept && editable) && note.trim().length < 10 && <p className="form-hint">Say what the customer should check or add, in at least 10 characters, to send the request.</p>}
  </div>;
  return (
    <section className="workspace-section research-panel" aria-label="Property research">
      <header className={`research-turn research-turn-${turn}`}>
        <span className="research-eyebrow">Research</span>
        <h2>{loading ? 'Loading research…' : turnTitle[turn]}</h2>
        {!loading && <p className="research-turn-detail">{turnText[turn]}</p>}
        {job && <p>{job.parentJobId ? 'Follow-up research' : 'Research'} of {job.input.address}, run {momentDateTime(job.createdAt)}.</p>}
        {runLeads && <div className="form-actions"><button className="button" disabled={busy} onClick={() => void retry()}>{busy ? 'Starting…' : 'Run research'}</button></div>}
      </header>
      {error && <div role="alert" className="alert error">{error}</div>}
      {ruleCoverage && <section className="inline-card" aria-label="Waiting for a rule">
        <h3>Waiting for a notice rule</h3>
        {ruleCoverage.reason === 'NO_DATING_RULE'
          ? <p>This notice has no due date yet. {coverageReasons.NO_DATING_RULE} Once staff approve a rule for {ruleCoverage.jurisdiction.replace('US-', '')} on Notice rules, the notice gets its date and the customer is emailed to confirm it.</p>
          : <p>Research has not started. {coverageReasons[ruleCoverage.reason] ?? ruleCoverage.reason} It starts automatically once staff record a rule for {ruleCoverage.jurisdiction.replace('US-', '')} that lists who the notice goes to.</p>}
      </section>}
      {job?.status === 'QUEUED' && <p>The job is saved. A connected worker will pick it up automatically.</p>}
      {job?.failureCode === 'PROJECT_FACTS_CHANGED' && <p>Project details changed after this job was queued. Start a new research request to use the updated details.</p>}
      {job?.failureCode === 'OPERATOR_EVIDENCE_OCR_FAILED' && <p>The uploaded PDF could not be completely read. Upload a clear official PDF and submit the completion again.</p>}
      {job?.failureCode === 'OPERATOR_EVIDENCE_NOT_GROUNDED' && <p>At least one entered value or exact quote was not present on its cited page. Check the page numbers and copy the evidence text exactly, then submit again.</p>}
      {job?.result && <>
        {/* Blocking items first: they must be dealt with before anything else. */}
        {/* A missing recipient is shown in its slot below, where the customer or staff fill it; other handoffs block the research. */}
        {job.result.operatorHandoff && (selecting || turn === 'empty' || job.result.operatorHandoff.reason !== 'RECIPIENTS_NOT_IDENTIFIED') && <section className="inline-card" aria-label="Operator handoff required">
          <h3>Operator handoff required</h3>
          <p>Automation stopped at {job.result.operatorHandoff.stage.replaceAll('-', ' ')}. Complete these steps before accepting the research.</p>
          <ol>{job.result.operatorHandoff.instructions.map((instruction, index) => <li key={index}>{instruction}</li>)}</ol>
          {job.result.operatorHandoff.evidenceFile && <button className="button button-quiet" onClick={() => void download(job.result!.operatorHandoff!.evidenceFile!)}>Open handoff evidence</button>}
          {job.result.operatorHandoff.sourceUrl && <p><a href={job.result.operatorHandoff.sourceUrl} target="_blank" rel="noreferrer">Open official source</a></p>}
        </section>}
        {!!job.result.revalidations?.length && <section className="inline-card" aria-label="Conflicting guidance">
          <h3>Guidance needs revalidation</h3>
          <p>Review the cited conflict and the guidance below. Run follow-up research to resolve it before accepting findings.</p>
          {job.result.revalidations.map(request => <article key={request.versionId}>
            <p>Guidance version: {request.versionId}</p><p>{request.reason}</p>
            {request.sources.map((source, index) => <div key={index}>
              <blockquote>{source.quote}</blockquote>
              <button type="button" onClick={() => void download(source.file)}>Open {source.file}{source.page ? `, page ${source.page}` : ''}</button>
            </div>)}
          </article>)}
        </section>}
        {(selecting || turn === 'empty') && <p className="research-summary">{job.result.summary}</p>}
        {turn === 'empty' && (() => {
          // Research's own account of what stopped it says more than its flags; generic review flags add nothing when nothing was found.
          const reasons = [...(job.result.limitations ?? []), ...job.result.flags.filter(flag => !reviewOnlyFlags.has(flag)).map(warningLabel)];
          return reasons.length > 0 && <section className="research-step" aria-label="What went wrong">
            <h3>What went wrong</h3>
            <ul className="research-reasons">{reasons.map((reason, index) => <li key={index}>{reason}</li>)}</ul>
            {!!job.result.unansweredQuestions?.length && <><h4>Still open</h4><ul className="research-reasons">{job.result.unansweredQuestions.map((item, index) => <li key={index}>{item}</li>)}</ul></>}
          </section>; })()}
        {turn !== 'empty' && (!!job.result.limitations?.length || !!job.result.unansweredQuestions?.length) && <details className="research-open-points fold"><summary>What research could not settle ({(job.result.limitations?.length ?? 0) + (job.result.unansweredQuestions?.length ?? 0)})</summary>
          {(job.result.limitations?.length ?? 0) > 0 && <><h3>Research limitations</h3><ul>{job.result.limitations!.map((item, index) => <li key={index}>{item}</li>)}</ul></>}
          {(job.result.unansweredQuestions?.length ?? 0) > 0 && <><h3>Unanswered questions</h3><ul>{job.result.unansweredQuestions!.map((item, index) => <li key={index}>{item}</li>)}</ul></>}
        </details>}
        {turn !== 'empty' && (() => { const findings = <>
        {(!!job.result.noticeRecipients?.length || !!job.result.unresolvedRecipients?.length) && <section aria-label="Notice recipients" className={`research-step${selecting && recipientsDone ? ' done' : ''}`}>
          <h3>{selecting && <span className="step-marker" aria-hidden="true">{recipientsDone ? '✓' : '•'}</span>}Who the notice goes to</h3>
          <p>As research found them{ruleApplies ? '; the customer confirms or corrects each one' : ''}. Check each against its source before relying on it.</p>
          <ul className="research-candidates">{job.result.noticeRecipients?.map(recipient => <li key={`${recipient.role}-${recipient.name}`}>
            <label>{selecting && <input type="checkbox" disabled={!editable} checked={confirmedRecipients.includes(recipientKey(recipient))}
              onChange={() => toggle(recipientKey(recipient), confirmedRecipients, setConfirmedRecipients)} />}
              <span><strong>{recipientLabel(recipient.role)}:</strong> <span title={recipient.name}>{displayCase(recipient.name)}</span></span></label>
            {recipient.address && <small title={recipient.address}>{displayCase(recipient.address)}</small>}
            <button type="button" className="table-action" onClick={() => void download(recipient.sourceFile)}>Open {recipient.sourceFile}</button>
          </li>)}</ul>
          {!!job.result.unresolvedRecipients?.length && <>
            <h4>Not yet identified</h4>
            <ul>{job.result.unresolvedRecipients.map((recipient, index) => <li key={`${recipient.role}-${index}`}><strong>{recipientLabel(recipient.role)}:</strong> {recipient.reason}</li>)}</ul>
          </>}
        </section>}
        {(!!job.result.owners.length || !ruleApplies) && <section aria-label="Property" className={`research-step${!ruleApplies && parcels.length ? ' done' : ''}`}>
          <h3>{!ruleApplies && <span className="step-marker" aria-hidden="true">{parcels.length ? '✓' : '1'}</span>}{ruleApplies ? 'Property found' : 'Choose the job property'}</h3>
          {!job.result.owners.length && <p>No property was identified.</p>}
          <ul className="research-candidates">{job.result.owners.map((owner) => <li key={owner.parcel}>
            <label>{selecting && <input type="checkbox" disabled={!editable} checked={parcels.includes(owner.parcel)} onChange={() => toggle(owner.parcel, parcels, setParcels)} />}
              <span><span title={owner.owner}>{displayCase(owner.owner)}</span> — {owner.parcel}</span></label>
            <span className={`match-badge ${owner.addressMatch === 'EXACT' ? 'good' : 'check'}`}>{owner.addressMatch === 'EXACT' ? 'Address matches the job' : 'Property association needs review'}</span>
            <small title={owner.siteAddress}>Property: {displayCase(owner.siteAddress)}</small><small title={owner.mailingAddress}>Mailing: {displayCase(owner.mailingAddress)}</small>
            <details><summary>Evidence</summary>
              <button className="button button-quiet" onClick={() => void download(owner.sourceFile)}>Open owner evidence</button>
              {owner.sourceUrl && <p><a href={owner.sourceUrl} target="_blank" rel="noreferrer">Open parcel source</a></p>}
            </details>
          </li>)}</ul>
        </section>}
        {(!!job.result.documents.length || !ruleApplies) && <section aria-label="Notices of commencement" className={`research-step${!ruleApplies && instruments.length ? ' done' : ''}`}>
          <h3>{!ruleApplies && <span className="step-marker" aria-hidden="true">{instruments.length ? '✓' : '2'}</span>}{ruleApplies ? 'Notice of commencement found' : 'Choose the notice of commencement'}</h3>
          {!job.result.documents.length && <p>No notice of commencement was retrieved.</p>}
          {!!job.result.documents.length && !job.result.documents.some(linkedToProperty) && <p>None of the {job.result.documents.length} notices the search returned is tied to this property.</p>}
          <ul className="research-candidates">{job.result.documents.filter(linkedToProperty).map((doc) => <li key={doc.instrument}>
            <label>{selecting && <input type="checkbox" disabled={!editable || doc.propertyMatch === 'NOT_ESTABLISHED'} checked={instruments.includes(doc.instrument)}
              onChange={() => toggle(doc.instrument, instruments, setInstruments)} />}
              <span><span title={doc.title}>{displayCase(doc.title)}</span> — {doc.instrument}</span></label>
            {doc.recordedOn && <small>Recorded: {doc.recordedOn}</small>}
            {!!doc.matchingParcels?.length && <small>Matched parcel: {doc.matchingParcels.join(', ')}</small>}
            {doc.propertyMatch && <span className={`match-badge ${doc.propertyMatch === 'NOT_ESTABLISHED' ? 'bad' : doc.propertyMatch === 'SOLE_CANDIDATE_OWNER_MATCH' ? 'check' : 'good'}`}>{doc.propertyMatch === 'NOT_ESTABLISHED'
              ? 'No property match established; this notice cannot be accepted.'
              : doc.propertyMatch === 'PARCEL_ID_MATCH' ? 'The notice names a matching parcel number.'
                : doc.propertyMatch === 'STREET_ADDRESS_MATCH' ? 'The notice names the job street address.'
                  : 'The notice names the owner; confirm the property association.'}</span>}
            <details><summary>Evidence</summary>
              <button className="button button-quiet" onClick={() => void download(doc.pdfFile)}>Open NOC PDF</button>
              {doc.sourceUrl && <p><a href={doc.sourceUrl} target="_blank" rel="noreferrer">Open notice source</a></p>}
              {doc.indexRow && <details>
                <summary>Clerk index record</summary>
                <p style={{ whiteSpace: 'pre-wrap' }}>{doc.indexRow}</p>
                {doc.indexUrl && <p><a href={doc.indexUrl} target="_blank" rel="noreferrer">Open clerk search</a></p>}
                {doc.indexSourceFile && <button className="button button-quiet" onClick={() => void download(doc.indexSourceFile!)}>Open clerk search capture</button>}
              </details>}
              {doc.propertyMatchSources?.map((source, index) => <details key={`${source.file}:${index}`}>
                <summary>Property-match citation{source.page ? `, page ${source.page}` : ''}</summary>
                {source.quote && <blockquote>{source.quote}</blockquote>}
                <button className="button button-quiet" onClick={() => void download(source.file)}>Open cited page</button>
              </details>)}
              {doc.fields.map((field) => <details key={field.name}>
                <summary>{field.name.replace(/([A-Z])/g, ' $1')}</summary>
                <p style={{ whiteSpace: 'pre-wrap' }}>{field.text}</p>
                {field.sources.map((source, index) => <button className="button button-quiet" key={index} onClick={() => void download(source.file)}>Source page {source.page}</button>)}
              </details>)}
            </details>
          </li>)}</ul>
          {job.result.documents.some(doc => !linkedToProperty(doc)) && <details className="fold"><summary>{job.result.documents.filter(doc => !linkedToProperty(doc)).length} other notices in the search, none tied to this property</summary>
            <ul className="other-notices">{job.result.documents.filter(doc => !linkedToProperty(doc)).map(doc => <li key={doc.instrument}>
              <span>{doc.recordedOn ?? 'Date not read'}</span><span>{doc.instrument}</span>
              <button type="button" className="link-button" onClick={() => void download(doc.pdfFile)}>Open notice</button></li>)}</ul></details>}
        </section>}
        {!!job.result.flags.length && <section aria-label="Warnings" className={`research-step${selecting && warningsDone ? ' done' : ''}`}>
          <h3>{selecting && <span className="step-marker" aria-hidden="true">{warningsDone ? '✓' : ruleApplies ? '•' : '3'}</span>}{ruleApplies || !selecting ? 'Warnings' : 'Acknowledge each warning'}</h3>
          <ul className="research-candidates">{job.result.flags.map((flag) => <li key={flag}><label>
            {selecting && <input type="checkbox" disabled={!editable} checked={flags.includes(flag)} onChange={() => toggle(flag, flags, setFlags)} />}
            <span>{warningLabel(flag)}</span></label></li>)}</ul>
        </section>}
        </>;
        // Findings are the work only while someone is deciding on them; otherwise they fold to one line.
        const counts = [[job.result.noticeRecipients?.length ?? 0, 'recipient'], [job.result.owners.length, 'property', 'properties'], [job.result.documents.length, 'notice'], [job.result.flags.length, 'warning']] as Array<[number, string, string?]>;
        const found = counts.slice(0, 3).filter(([n]) => n).map(([n, one, many]) => `${n} ${n === 1 ? one : many ?? `${one}s`}`).join(', ');
        const warnings = job.result.flags.length ? ` (${job.result.flags.length} ${job.result.flags.length === 1 ? 'warning' : 'warnings'})` : '';
        if (selecting) return findings;
        const record = <>
          <ResearchRecord result={job.result} work={job.input.workDescription} confirmation={confirmation} required={(job.input as { job?: { requiredRecipients?: Array<{ role: string; when: string; condition: string | null }> } }).job?.requiredRecipients ?? null}
            onOpen={file => void download(file)} />
          {!!job.result.flags.length && <details className="fold"><summary>What research flagged ({job.result.flags.length})</summary>
            <ul className="research-reasons">{job.result.flags.map(flag => <li key={flag}>{warningLabel(flag)}</li>)}</ul></details>}
        </>;
        // Research from before the rule is history: folded, so running it again leads.
        return predatesRule ? <details className="fold research-findings"><summary>What the earlier research found: {found || 'nothing usable'}{warnings}</summary>{record}</details> : record; })()}
      </>}
      {/* The decision leads when staff decide; when the customer confirms, staff can still accept on their behalf.
          Research that predates the rule is simply run again, so it offers no decision. */}
      {finished && orderStatus === 'RESEARCH' && turn !== 'rerun' && (turn === 'staff' || turn === 'empty'
        ? decision
        : <><h3 className="step-in">If you need to step in</h3><details className="fold" onToggle={event => setActingForCustomer((event.target as HTMLDetailsElement).open)}><summary>Act on the customer’s behalf: confirm the findings or request details</summary><p>Only when the customer has asked PayRight to. Opening this shows the findings above for selection.</p>{decision}</details></>)}
      {job?.reviews.map((review, index) => <article key={index} className="research-review">
        <h3>{review.decision === 'ACCEPT' ? 'Accepted research selection' : 'Requested information'}</h3>
        <p>{review.note}</p>
        <p>Parcels: {review.parcelIds.join(', ') || 'None'} · NOCs: {review.instruments.join(', ') || 'None'}</p>
      </article>)}
      {orderStatus === 'RESEARCH' && (!job || !['RUNNING', 'QUEUED'].includes(job.status)) && <details className="research-more fold">
        <summary>More research options</summary>
        {!runLeads && <p><button className="button button-quiet" disabled={busy} onClick={() => void retry()}>{job ? 'Run research again' : 'Run research'}</button></p>}
        {aiEnabled && job && !aiAvailable && <p>Research with your own instruction uses AI time, and no AI allowance is in force. Staff approve one on the AI research page, or complete the research with official documents.</p>}
        {aiAvailable && job && <details>
          <summary>Research further</summary>
          <label>What should the researcher investigate next?
            <textarea value={instruction} minLength={10} maxLength={4000} onChange={event => setInstruction(event.target.value)} />
          </label>
          <p>The new run will retain this instruction and the prior findings, source references and reviewed corrections.</p>
          <button className="button" disabled={busy || instruction.trim().length < 10} onClick={() => void followup()}>Queue follow-up research</button>
          {instruction.trim().length < 10 && <p className="form-hint">Describe what to investigate, in at least 10 characters.</p>}
        </details>}
        {job && job.strategy !== 'OPERATOR' && <details><summary>Complete with official evidence</summary>
          <form className="inline-card" onSubmit={completeManually}>
            <p>Upload the official parcel record and recorded NOC. The worker will OCR both files and reject any value or quotation the evidence does not contain.</p>
            <label>Official parcel PDF<input required name="parcelFile" type="file" accept="application/pdf,.pdf" /></label>
            <label>Parcel ID<input required name="parcel" /></label><label>Owner<input required name="owner" /></label>
            <label>Site address<input name="siteAddress" /></label><label>Mailing address<input name="mailingAddress" /></label>
            <label>Address match<select name="addressMatch" defaultValue="EXACT"><option>EXACT</option><option>PARTIAL</option><option>CONFLICT</option><option>UNVERIFIED</option></select></label>
            <label>Parcel evidence page<input required name="parcelPage" type="number" min="1" defaultValue="1" /></label>
            <label>Exact parcel evidence quote<textarea required name="parcelQuote" minLength={5} /></label>
            <label>Official NOC PDF<input required name="nocFile" type="file" accept="application/pdf,.pdf" /></label>
            <label>Instrument number<input required name="instrument" /></label><label>Document title<input required name="title" defaultValue="NOTICE OF COMMENCEMENT" /></label>
            <label>NOC evidence page<input required name="nocPage" type="number" min="1" defaultValue="1" /></label>
            <label>Exact NOC evidence quote<textarea required name="nocQuote" minLength={5} /></label>
            <label>Explain the property and address match<textarea required name="matchExplanation" minLength={10} /></label>
            <button className="button" disabled={busy}>{busy ? 'Validating…' : 'Submit for separate review'}</button>
          </form>
        </details>}
      </details>}
      {job && <details className="research-record fold">
        <summary>Research record: sources, run log, guidance and corrections</summary>
        {job.instruction && <p><strong>Instruction for this run:</strong> {job.instruction}</p>}
        <ResearchReviewTrail jobs={jobs} onDownload={downloadArtifact} />
        <ResearchRunLog key={`log:${job.id}`} jobId={job.id} />
        <ResearchKnowledge key={`knowledge:${job.id}`} orderId={orderId} jobId={job.id} state={job.input.state} county={job.input.county} refreshVersion={version}
          artifacts={job.artifacts} onDownload={downloadArtifact} onChange={() => { setVersion(v => v + 1); onChange(); }} />
        <ResearchCorrections key={job.id} orderId={orderId} jobId={job.id}
          editable={orderStatus === 'RESEARCH' && ['REVIEW_REQUIRED', 'ACCEPTED'].includes(job.status)}
          artifacts={job.artifacts} onDownload={downloadArtifact} onChange={() => { setVersion(v => v + 1); onChange(); }}
          targets={[
            ...(job.result?.owners ?? []).flatMap(owner => [
              { label: `Owner · ${owner.parcel}`, target: { kind: 'OWNER', candidate: owner.parcel }, value: owner.owner },
              { label: `Parcel · ${owner.parcel}`, target: { kind: 'PARCEL', candidate: owner.parcel }, value: owner.parcel },
              { label: `Site address · ${owner.parcel}`, target: { kind: 'SITE_ADDRESS', candidate: owner.parcel }, value: owner.siteAddress },
              { label: `Mailing address · ${owner.parcel}`, target: { kind: 'MAILING_ADDRESS', candidate: owner.parcel }, value: owner.mailingAddress },
            ]),
            ...(job.result?.documents ?? []).flatMap(document => [
              { label: `Instrument · ${document.instrument}`, target: { kind: 'NOC_INSTRUMENT', candidate: document.instrument }, value: document.instrument },
              ...document.fields.map(field => ({ label: `${field.name} · ${document.instrument}`, target: { kind: 'DOCUMENT_FIELD', candidate: document.instrument, field: field.name }, value: field.text })),
            ]),
          ]} />
        {jobs.length > 1 && <details>
          <summary>Earlier research runs ({jobs.length - 1})</summary>
          {jobs.slice(1).map((earlier) => <article key={earlier.id}>
            <p>{momentDateTime(earlier.createdAt)} — {labels[earlier.status] ?? earlier.status}</p>
            {earlier.reviews.map((r, i) => <p key={i}>{r.note}</p>)}
            {earlier.instruction && <p>Follow-up: {earlier.instruction}</p>}
            <ResearchRunLog jobId={earlier.id} />
          </article>)}
        </details>}
      </details>}
    </section>
  );
}
